import { Form } from "@inertiajs/react"
import { Loader2 } from "lucide-react"
import {
  type ChangeEvent,
  type FocusEvent,
  type ReactNode,
  type RefObject,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
} from "@/components/responsive-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"
import { adminInvitationsPath } from "@/routes"

export type InviteFirstAdminTenant =
  | { kind: "brand"; id: number; name: string }
  | { kind: "agency"; id: number; name: string }

interface InviteFirstAdminDialogProps {
  /**
   * Tenant context — drives:
   *   - The hidden `brand_id` or `agency_id` body field POSTed alongside
   *     `path: "first_admin"` (backend routes `Admin::Invitation::Send
   *     ForExistingTenant` from this).
   *   - Dialog title interpolation (`Invite first admin to {tenant}`).
   *
   * Discriminated union keeps the brand-vs-agency distinction at the
   * type level — the component never renders both inputs simultaneously.
   */
  tenant: InviteFirstAdminTenant
  /** Trigger element passed via `<DialogTrigger asChild>`. */
  trigger: ReactNode
  /** Optional ref forwarded for explicit focus return on close. */
  triggerRef?: RefObject<HTMLElement | null>
}

/**
 * OKS-12 Path D — Invite first admin to an EXISTING tenant.
 *
 * Mounts on `Admin/{Brands,Agencies}/Show` from the orphan empty-state
 * branch of `PendingFirstAdminInviteCard` (when `team.length === 0 &&
 * pending_first_admin_invite === null`). Server-driven
 * `can_invite_first_admin` prop gates the parent CTA visibility — by
 * the time this dialog mounts, backend has confirmed the tenant is in
 * an orphan state.
 *
 * Single email field — no name/locale, since the tenant already exists
 * (locale is inherited from the brand/agency record server-side per UX
 * Spec line 22). POSTs to `Admin::InvitationsController#create` with
 * the discriminator `path: "first_admin"` + the tenant ID:
 *
 *   { invitation: { path: "first_admin", brand_id: 42, admin_email: "..." } }
 *   { invitation: { path: "first_admin", agency_id: 42, admin_email: "..." } }
 *
 * Backend's `Admin::Invitation::SendForExistingTenant` service handles
 * both branches; FE just discriminates which hidden ID input to render.
 *
 * Crew form rules: Inertia `<Form>` (NOT shadcn `Form`); plain `Input` +
 * `Label` + `<InputError>`; no react-hook-form; no zod. Email auto
 * trim+lowercase on blur (parity with the other invite dialogs). Same
 * non-dismissable + triggerRef + 5-state envelope as `InviteAdminDialog`
 * and `NewTenantDialog`.
 *
 * Locked QA testid:
 *   - `admin-invite-first-admin-trigger` on the trigger element
 *   - `admin-invite-first-admin-dialog` on the DialogContent
 *   - `admin-invite-first-admin-submit` on the primary button
 */
export function InviteFirstAdminDialog({
  tenant,
  trigger,
  triggerRef,
}: InviteFirstAdminDialogProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState("")
  /**
   * Mid-submit guard for `onOpenChange` — same pattern as
   * `InviteAdminDialog` / `NewTenantDialog`. Mirrors `processing` from
   * the render-prop into a ref every render so ESC + overlay-click can
   * read the latest value outside the Form scope.
   */
  const submittingRef = useRef(false)

  function handleEmailChange(event: ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value)
  }

  function handleEmailBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.target.value.trim().toLowerCase()
    if (next !== email) setEmail(next)
  }

  function handleClose() {
    setOpen(false)
    setEmail("")
    triggerRef?.current?.focus()
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && submittingRef.current) return
        if (!next) handleClose()
        else setOpen(true)
      }}
    >
      {/*
       * Radix Slot lets the child Button's `data-testid` win. Parent
       * page sets the locked testid on the Button itself — for the
       * orphan empty-state CTA, that's
       * `admin-pending-first-admin-empty-cta`.
       */}
      <ResponsiveDialogTrigger asChild>{trigger}</ResponsiveDialogTrigger>
      <ResponsiveDialogContent
        className="sm:max-w-md"
        data-testid="admin-invite-first-admin-dialog"
      >
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>
            {t("admin.invites.dialog.invite_first_admin.title", {
              tenant: tenant.name,
            })}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription className="sr-only">
            {t("admin.invites.dialog.invite_first_admin.description")}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>

        {/* OKS-120 — Form acts as scrollable body in mobile Sheet mode. */}
        <Form
          action={adminInvitationsPath()}
          method="post"
          options={{ preserveScroll: true }}
          onSuccess={handleClose}
          resetOnSuccess
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-0"
        >
          {({ processing, errors }) => {
            submittingRef.current = processing
            return (
              <>
                <input type="hidden" name="path" value="first_admin" />
                {/*
                 * Tenant-ID hidden input — exactly one of `brand_id` /
                 * `agency_id` per backend strong-params contract. The
                 * discriminated union on `tenant` makes this safe at the
                 * type level (no possibility of both-present or
                 * neither-present).
                 */}
                {tenant.kind === "brand" ? (
                  <input
                    type="hidden"
                    name="brand_id"
                    value={tenant.id}
                    data-testid="admin-invite-first-admin-brand-id"
                  />
                ) : (
                  <input
                    type="hidden"
                    name="agency_id"
                    value={tenant.id}
                    data-testid="admin-invite-first-admin-agency-id"
                  />
                )}

                {errors.base && (
                  <div
                    role="alert"
                    className="border-destructive/40 bg-destructive/10 text-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="admin-invite-first-admin-error-banner"
                  >
                    {Array.isArray(errors.base)
                      ? errors.base.join(" ")
                      : errors.base}
                  </div>
                )}
                {!online && (
                  <div
                    className="border-muted-foreground/20 bg-muted/40 text-muted-foreground rounded-md border px-3 py-2 text-sm"
                    data-testid="admin-invite-first-admin-offline-banner"
                  >
                    {t("admin.invites.dialog.offline_banner")}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Label htmlFor="invite-first-admin-email">
                    {t("admin.invites.dialog.invite_first_admin.email_label")}
                    <span
                      className="text-destructive ml-0.5"
                      aria-hidden="true"
                    >
                      *
                    </span>
                  </Label>
                  <Input
                    id="invite-first-admin-email"
                    name="admin_email"
                    type="email"
                    autoComplete="off"
                    autoFocus
                    aria-required="true"
                    value={email}
                    onChange={handleEmailChange}
                    onBlur={handleEmailBlur}
                    placeholder={t(
                      "admin.invites.dialog.invite_first_admin.email_placeholder",
                    )}
                    aria-invalid={errors.admin_email ? "true" : undefined}
                    aria-describedby={
                      errors.admin_email
                        ? "invite-first-admin-email-error"
                        : undefined
                    }
                    disabled={!online || processing}
                  />
                  <InputError
                    messages={errors.admin_email}
                    id="invite-first-admin-email-error"
                  />
                </div>

                <ResponsiveDialogFooter>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={processing}
                    aria-disabled={processing ? "true" : undefined}
                  >
                    {t("admin.invites.dialog.invite_first_admin.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    disabled={processing || !online}
                    aria-disabled={processing || !online ? "true" : undefined}
                    data-testid="admin-invite-first-admin-submit"
                  >
                    {processing && (
                      <Loader2
                        className="size-4 animate-spin"
                        aria-hidden="true"
                      />
                    )}
                    {processing
                      ? t("admin.invites.dialog.invite_first_admin.sending")
                      : t("admin.invites.dialog.invite_first_admin.submit")}
                  </Button>
                </ResponsiveDialogFooter>
              </>
            )
          }}
        </Form>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
